import { getApiBaseUrl, reloadRuntimeConfig } from '@/integrations/api/config';
import {
  getAccessToken,
  getActiveTenantId,
  isTokenExpiringSoon,
  setAccessToken,
} from '@/integrations/api/token_storage';
import { getDeviceFingerprint } from '@/lib/deviceFingerprint';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  tenantId?: string | null;
  auth?: boolean;
  /** Skip automatic token refresh (login/MFA/register). */
  skipRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const token = getAccessToken();
  if (!token) return false;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const headers = new Headers({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        });
        headers.set('X-Device-Fingerprint', getDeviceFingerprint());
        const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh/users`, {
          method: 'POST',
          headers,
        });
        const text = await response.text();
        const data = text ? (JSON.parse(text) as { token?: { value: string; expiresAt: string | null } }) : null;
        if (!response.ok || !data?.token?.value) {
          setAccessToken(null);
          return false;
        }
        setAccessToken(data.token.value, data.token.expiresAt ?? null);
        return true;
      } catch {
        setAccessToken(null);
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

async function ensureFreshToken(): Promise<boolean> {
  const token = getAccessToken();
  if (!token) return false;
  if (!isTokenExpiringSoon()) return true;
  return refreshAccessToken();
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { body, tenantId, auth = true, skipRefresh = false, headers: initHeaders, ...rest } = options;

  if (auth && !skipRefresh) {
    await ensureFreshToken();
  }

  const doFetch = async (retryAfter401 = false): Promise<Response> => {
    const headers = new Headers(initHeaders);

    if (body !== undefined && !(body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    if (auth) {
      const token = getAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    // Explicit tenantId wins; otherwise fall back to the active tenant so every
    // tenant-scoped endpoint receives X-Tenant-Id even when callers omit it.
    const effectiveTenant = tenantId ?? getActiveTenantId();
    if (effectiveTenant) headers.set('X-Tenant-Id', effectiveTenant);
    headers.set('X-Device-Fingerprint', getDeviceFingerprint());

    return fetch(`${getApiBaseUrl()}${path}`, {
      ...rest,
      headers,
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  // Network-level failure (DNS, CORS, offline, API down): the browser only says
  // "Failed to fetch". Re-read /runtime-config.js in case the API address changed
  // since the page was loaded, retry once, and otherwise raise a clear error that
  // names the API URL so the user (and support) know what could not be reached.
  const fetchOrExplain = async (): Promise<Response> => {
    try {
      return await doFetch();
    } catch (err) {
      if (err instanceof TypeError || (err as { name?: string })?.name === 'TypeError') {
        const changed = await reloadRuntimeConfig();
        if (changed) {
          try {
            return await doFetch();
          } catch {
            /* fall through */
          }
        }
        throw new ApiError('network_error', 0, { apiUrl: getApiBaseUrl(), path });
      }
      throw err;
    }
  };

  let response = await fetchOrExplain();

  if (auth && !skipRefresh && response.status === 401 && getAccessToken()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await doFetch(true);
  }

  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      // Plain-text body (e.g. a proxy error page such as "no available server").
      data = { message: text.slice(0, 200) };
    }
  }

  if (!response.ok) {
    const message =
      typeof data === 'object' && data !== null && 'message' in data
        ? String((data as { message: string }).message)
        : response.statusText;
    throw new ApiError(message, response.status, data);
  }

  return data as T;
}
